import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../state'
import * as dms from '../../dms'

import { getActionsConfig } from './actions'
import { Selectors } from './selectors'
import { State } from './state'

type Definition = state.admin.editingVariables.models.Definition

/**
 * Feature for editing variable definitions
 */
@ngCore.Injectable({ providedIn: 'root' })
export class Feature
  extends store.Feature<State, Selectors, ReturnType<typeof getActionsConfig>>
  implements ds.table.models.Config<Definition>
{
  /** Table columns for the variables currently being edited. */
  public readonly columns: readonly ds.table.models.Column<Definition>[] = [
    {
      getCell: ({ row }: ds.table.models.Column.Cell.Context<Definition>) => ({
        value: this._getDisplayValue(row.id),
      }),
      id: 'id',
      label: 'ID',
    },
    {
      getCell: ({ row }: ds.table.models.Column.Cell.Context<Definition>) => ({
        value: this._getDisplayValue(row.name),
      }),
      id: 'name',
      label: 'Name',
    },
    {
      getCell: ({ row }: ds.table.models.Column.Cell.Context<Definition>) => ({
        value: ents.core.common.value.types.Intrinsic.isIntrinsic(row.type)
          ? row.type
          : row.type
            ? 'Other'
            : undefined,
      }),
      id: 'type',
      label: 'Type',
    },
    {
      getCell: ({ row }: ds.table.models.Column.Cell.Context<Definition>) => ({
        value: this._getDisplayValue(row.defaultValue),
      }),
      id: 'defaultValue',
      label: 'Default value',
    },
  ]

  /** Presentation options for the variable definitions table. */
  public readonly layout: ds.table.models.Layout = {
    showColumnSettings: false,
    title: 'Variables',
  }

  /** Tools that create a new variable definition. */
  public readonly tools: ds.table.models.Tool.Config = {
    create: {
      iconClass: 'atn-icon atn-plus atn-blue',
      id: 'create-variable',
      onClick: () =>
        this.actions.setFocused
          .from('VariablesTable.Icons.Add.Clicked')
          .dispatch({}),
      title: 'Create variable',
    },
  }

  /**
   * Current variable rows. Reading the definitions signal here keeps this
   * config branch reactive while the feature remains the stable table config.
   */
  public get rows(): ds.table.models.Row.Config<Definition> {
    return {
      describe: ({ row, rowId }: ds.table.models.Row.Context<Definition>) => ({
        tools: [
          {
            iconClass: 'atn-icon atn-edit atn-blue',
            id: `${rowId}-edit`,
            onClick: () =>
              this.actions.setFocused
                .from(`VariablesTable.Row-${row.id}.Icons.Edit.Clicked`)
                .dispatch({ id: row.id }),
            title: 'Edit',
          },
          {
            iconClass: 'atn-icon atn-trash atn-red',
            id: `${rowId}-delete`,
            onClick: () =>
              this.actions.removeDefinition
                .from(`VariablesTable.Row-${row.id}.Icons.Delete.Clicked`)
                .dispatch(row.id),
            title: 'Delete',
          },
        ],
      }),
      getId: definition => definition.id,
      items: this.selectors.definitionsList.asSignal(),
    }
  }

  constructor() {
    const _schemaFeature = ngCore.inject(dms.schema.Feature)

    super({
      actions: getActionsConfig(),
      initialState: State.INITIAL,
      name: 'EditingVariables',
      selectors: () => new Selectors(this, _schemaFeature),
    })
  }

  /** Formats a definition value as text for the variable definitions table. */
  private _getDisplayValue(value: unknown): string | undefined {
    if (TypeChecker.isNullish(value)) {
      return undefined
    }

    if (TypeChecker.isObject(value)) {
      return JSON.stringify(value)
    }

    if (
      TypeChecker.isString(value) ||
      TypeChecker.isNumber(value) ||
      TypeChecker.isBigInt(value) ||
      TypeChecker.isBoolean(value) ||
      TypeChecker.isSymbol(value)
    ) {
      return value.toString()
    }

    return undefined
  }
}
